import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeleteLocationDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/delete-location.form-default-values';
import { DeleteLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/delete-location.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoDeleteLocationMutation } from '@app/core/service/cpms-bo-location.service';

import {
  DeleteLocationContextProvider,
  useLocationContext,
} from '../location.context';
import { LocationDeleteFormController } from './location-delete-form/location-delete-form.controller';

interface LocationDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const LocationDeleteController = ({
  onCancel,
  onSuccess,
}: LocationDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deleteLocationMutation] = useCpmsBoDeleteLocationMutation();
  const location = useLocationContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<Pick<DeleteLocationModel, 'id'>> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(t('$.location-delete.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<DeleteLocationModel, 'id'>
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    deleteLocationMutation({
      id: fieldValues.id,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.location-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.location-delete.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeleteLocationDefaultValues(location.id);

  return (
    <DeleteLocationContextProvider data={data}>
      <LocationDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteLocationContextProvider>
  );
};
